import React from "react";

/**
 * Lightweight, env-gated render profiler for the Key Moments / Clips pages.
 *
 * Goal: understand what makes the *initial* page render expensive — how many
 * commits happen, how long each costs, and which component re-renders in a storm
 * (and which prop is responsible for it).
 *
 * Enable with env vars in `.env`:
 *   VITE_RENDER_PROFILING=true          → collect commit + render stats, dump a report
 *   VITE_RENDER_PROFILING_VERBOSE=true  → also log every individual render line (noisy)
 *
 * When VITE_RENDER_PROFILING is not "true" everything here is a no-op:
 *   - useRenderProfiler() returns immediately (only an always-called useRef runs)
 *   - <RenderProfiler> renders its children directly, with no React.Profiler wrapper
 *
 * The collected report is auto-dumped to the console ~800ms after the last commit
 * settles, and can be triggered/reset manually from the devtools console:
 *   __renderProfReport()   → print the current report
 *   __renderProfReset()    → clear all counters (call before a fresh measurement)
 */

export const RENDER_PROFILING_ENABLED =
    String(import.meta.env?.VITE_RENDER_PROFILING ?? "").toLowerCase() === "true";

const VERBOSE =
    String(import.meta.env?.VITE_RENDER_PROFILING_VERBOSE ?? "").toLowerCase() === "true";

// ── Registry ──────────────────────────────────────────────────────────────────
// component label -> render/prop-change stats
const componentStats = new Map();
// profiler id -> commit timing stats
const profilerStats = new Map();
// effect label -> wall-clock spent inside instrumented effect bodies
const effectStats = new Map();
// named timing marks (performance.now())
const marks = new Map();

function componentEntry(label) {
    let e = componentStats.get(label);
    if (!e) {
        e = { label, renders: 0, mounts: 0, updates: 0, propChanges: new Map() };
        componentStats.set(label, e);
    }
    return e;
}

function profilerEntry(id) {
    let e = profilerStats.get(id);
    if (!e) {
        e = { id, commits: 0, mounts: 0, updates: 0, totalMs: 0, maxMs: 0, totalBaseMs: 0 };
        profilerStats.set(id, e);
    }
    return e;
}

// ── Debounced auto-dump ─────────────────────────────────────────────────────────
let dumpTimer = null;
function scheduleDump() {
    if (!RENDER_PROFILING_ENABLED) return;
    if (dumpTimer) clearTimeout(dumpTimer);
    dumpTimer = setTimeout(() => {
        dumpTimer = null;
        dumpReport();
    }, 800);
}

/**
 * Record a timing mark. Use to bracket the fetch → render window, e.g.
 *   profMark("load:start") at the top of loadCollections()
 *   profMark("fetch:done") right after the fetch resolves
 *   profMark("render:settled") on the first commit after setUpdate(false)
 */
export function profMark(name) {
    if (!RENDER_PROFILING_ENABLED) return;
    marks.set(name, performance.now());
    // Also emit a real User Timing mark so it shows in the Chrome Performance
    // panel's "Timings" track — makes the render/paint window trivial to locate.
    try { performance.mark(name); } catch { /* ignore */ }
    if (VERBOSE) console.debug(`[renderProf] mark "${name}" @ ${performance.now().toFixed(1)}ms`);
    scheduleDump();
}

/**
 * Like profMark, but fires after the browser has committed AND painted the
 * pending update (double rAF + a macrotask). Use right after the state change
 * that triggers the heavy mount to bracket the full render+layout+paint window:
 * the gap between a "fetch-done" mark and this paint mark is the wall-clock cost
 * we're attributing to DOM volume.
 */
export function profMarkAfterPaint(name) {
    if (!RENDER_PROFILING_ENABLED) return;
    requestAnimationFrame(() =>
        requestAnimationFrame(() =>
            setTimeout(() => profMark(name), 0),
        ),
    );
}

/** Clear all collected stats and marks. Call before starting a fresh measurement. */
export function profReset() {
    componentStats.clear();
    profilerStats.clear();
    effectStats.clear();
    marks.clear();
    if (RENDER_PROFILING_ENABLED) console.info("[renderProf] reset");
}

/**
 * Time the synchronous body of an effect (or any function) and accumulate it
 * under `label`. Returns whatever `fn` returns (so it can wrap an effect body
 * that returns a cleanup function). No-op (just calls fn) when disabled.
 *
 * Use to attribute the commit's "Passive effects" total to specific effects:
 *   useEffect(() => profEffect("WipeCard:mount", () => { ...setup...; return cleanup; }), [])
 */
export function profEffect(label, fn) {
    if (!RENDER_PROFILING_ENABLED) return fn();
    const t0 = performance.now();
    try {
        return fn();
    } finally {
        const dt = performance.now() - t0;
        let e = effectStats.get(label);
        if (!e) {
            e = { label, runs: 0, totalMs: 0, maxMs: 0 };
            effectStats.set(label, e);
        }
        e.runs += 1;
        e.totalMs += dt;
        if (dt > e.maxMs) e.maxMs = dt;
        scheduleDump();
    }
}

/**
 * Per-component render tracker. Counts renders, splits mount vs update, and (on
 * updates) attributes each render to the prop(s) whose identity changed since the
 * previous render — the key signal for diagnosing a re-render storm.
 *
 * @param {string} label   stable component label, e.g. "ClipCard"
 * @param {object} [props] the props (or selected props) to watch for identity changes
 */
export function useRenderProfiler(label, props) {
    // Always call the hook (rules of hooks); do nothing when disabled.
    const prevProps = React.useRef(undefined);
    if (!RENDER_PROFILING_ENABLED) return;

    const entry = componentEntry(label);
    entry.renders += 1;

    if (prevProps.current === undefined) {
        entry.mounts += 1;
    } else {
        entry.updates += 1;
        const prev = prevProps.current;
        const cur = props || {};
        const keys = new Set([...Object.keys(prev), ...Object.keys(cur)]);
        const changed = [];
        keys.forEach((k) => {
            if (prev[k] !== cur[k]) {
                changed.push(k);
                entry.propChanges.set(k, (entry.propChanges.get(k) || 0) + 1);
            }
        });
        if (changed.length === 0) {
            // Re-rendered with identical props → parent re-render not stopped by memo.
            entry.propChanges.set("(none — parent rerender)", (entry.propChanges.get("(none — parent rerender)") || 0) + 1);
        }
        if (VERBOSE) {
            console.debug(`[renderProf] ${label} update #${entry.updates} changed:`, changed.length ? changed.join(", ") : "(none)");
        }
    }

    prevProps.current = props || {};
    scheduleDump();
}

// ── React.Profiler onRender callback ────────────────────────────────────────────
function onRenderCallback(id, phase, actualDuration, baseDuration) {
    const e = profilerEntry(id);
    e.commits += 1;
    if (phase === "mount") e.mounts += 1;
    else e.updates += 1;
    e.totalMs += actualDuration;
    e.totalBaseMs += baseDuration;
    if (actualDuration > e.maxMs) e.maxMs = actualDuration;
    if (VERBOSE) {
        console.debug(`[renderProf] <${id}> ${phase} commit#${e.commits} actual=${actualDuration.toFixed(1)}ms base=${baseDuration.toFixed(1)}ms`);
    }
    scheduleDump();
}

/**
 * Wrap a subtree to collect React commit timings for it. Renders children
 * directly (no overhead) when profiling is disabled.
 *
 * @param {string} id        profiler boundary id, e.g. "KeyMoments:CollectionList"
 * @param {React.ReactNode} children
 */
export function RenderProfiler({ id, children }) {
    if (!RENDER_PROFILING_ENABLED) return children;
    return (
        <React.Profiler id={id} onRender={onRenderCallback}>
            {children}
        </React.Profiler>
    );
}

// ── Report ────────────────────────────────────────────────────────────────────
export function dumpReport() {
    if (!RENDER_PROFILING_ENABLED) return;

    /* eslint-disable no-console */
    console.group("%c[renderProf] report", "color:#ff9800;font-weight:bold");

    // Commit timing per profiler boundary
    if (profilerStats.size) {
        const rows = {};
        let totalCommits = 0;
        let totalMs = 0;
        let maxMs = 0;
        for (const e of profilerStats.values()) {
            totalCommits += e.commits;
            totalMs += e.totalMs;
            if (e.maxMs > maxMs) maxMs = e.maxMs;
            rows[e.id] = {
                commits: e.commits,
                mounts: e.mounts,
                updates: e.updates,
                "totalActual(ms)": +e.totalMs.toFixed(1),
                "maxCommit(ms)": +e.maxMs.toFixed(1),
                "avgCommit(ms)": +(e.totalMs / e.commits).toFixed(1),
            };
        }
        console.log(
            `%cCommits: ${totalCommits} • total main-thread render: ${totalMs.toFixed(0)}ms • worst commit: ${maxMs.toFixed(0)}ms`,
            "color:#4caf50;font-weight:bold",
        );
        console.table(rows);
    }

    // Per-component render counts + prop-change attribution
    if (componentStats.size) {
        const rows = {};
        const sorted = [...componentStats.values()].sort((a, b) => b.renders - a.renders);
        for (const e of sorted) {
            const topProps = [...e.propChanges.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => `${k}×${v}`)
                .join(", ");
            rows[e.label] = {
                renders: e.renders,
                mounts: e.mounts,
                updates: e.updates,
                "changed props (by frequency)": topProps || "—",
            };
        }
        console.table(rows);
    }

    // Instrumented effect bodies (attributes the commit's "Passive effects" total)
    if (effectStats.size) {
        const rows = {};
        let total = 0;
        const sorted = [...effectStats.values()].sort((a, b) => b.totalMs - a.totalMs);
        for (const e of sorted) {
            total += e.totalMs;
            rows[e.label] = {
                runs: e.runs,
                "total(ms)": +e.totalMs.toFixed(1),
                "max(ms)": +e.maxMs.toFixed(1),
                "avg(ms)": +(e.totalMs / e.runs).toFixed(2),
            };
        }
        console.log(`%cInstrumented effects total: ${total.toFixed(0)}ms`, "color:#03a9f4;font-weight:bold");
        console.table(rows);
    }

    // Timing marks (relative to the earliest mark)
    if (marks.size) {
        const entries = [...marks.entries()].sort((a, b) => a[1] - b[1]);
        const base = entries[0][1];
        const rows = {};
        let prev = base;
        for (const [name, t] of entries) {
            rows[name] = {
                "t+(ms)": +(t - base).toFixed(1),
                "Δprev(ms)": +(t - prev).toFixed(1),
            };
            prev = t;
        }
        console.table(rows);
    }

    console.groupEnd();
    /* eslint-enable no-console */
}

// ── Plain-text report (copy-pasteable) ──────────────────────────────────────────
// console.table output can't be selected/copied in Chrome. This renders the same
// data as fixed-width text tables you can copy. From the console:
//   copy(__renderProfText())   → puts the whole report on your clipboard
//   __renderProfText()         → returns + logs the report as plain text

function formatTable(headers, rows) {
    // headers: string[]; rows: string[][]
    const all = [headers, ...rows];
    const widths = headers.map((_, c) => Math.max(...all.map((r) => String(r[c] ?? "").length)));
    const line = (r) => r.map((cell, c) => String(cell ?? "").padEnd(widths[c])).join("  ");
    const sep = widths.map((w) => "-".repeat(w)).join("  ");
    return [line(headers), sep, ...rows.map(line)].join("\n");
}

export function buildReportText() {
    if (!RENDER_PROFILING_ENABLED) return "[renderProf] disabled";

    const sections = [];
    const ts = new Date().toISOString();
    sections.push(`=== renderProf report @ ${ts} ===`);

    // Commit timing per profiler boundary
    if (profilerStats.size) {
        let totalCommits = 0;
        let totalMs = 0;
        let maxMs = 0;
        const rows = [];
        for (const e of profilerStats.values()) {
            totalCommits += e.commits;
            totalMs += e.totalMs;
            if (e.maxMs > maxMs) maxMs = e.maxMs;
            rows.push([
                e.id,
                e.commits,
                e.mounts,
                e.updates,
                e.totalMs.toFixed(1),
                e.maxMs.toFixed(1),
                (e.totalMs / e.commits).toFixed(1),
            ]);
        }
        sections.push(
            `Commits: ${totalCommits} | total render: ${totalMs.toFixed(0)}ms | worst commit: ${maxMs.toFixed(0)}ms`,
        );
        sections.push(
            formatTable(["boundary", "commits", "mounts", "updates", "totalActual(ms)", "maxCommit(ms)", "avgCommit(ms)"], rows),
        );
    }

    // Per-component render counts + prop-change attribution
    if (componentStats.size) {
        const rows = [];
        const sorted = [...componentStats.values()].sort((a, b) => b.renders - a.renders);
        for (const e of sorted) {
            const topProps = [...e.propChanges.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => `${k}×${v}`)
                .join(", ");
            rows.push([e.label, e.renders, e.mounts, e.updates, topProps || "—"]);
        }
        sections.push(formatTable(["component", "renders", "mounts", "updates", "changed props (by frequency)"], rows));
    }

    // Instrumented effect bodies
    if (effectStats.size) {
        const sorted = [...effectStats.values()].sort((a, b) => b.totalMs - a.totalMs);
        let total = 0;
        const rows = sorted.map((e) => {
            total += e.totalMs;
            return [e.label, e.runs, e.totalMs.toFixed(1), e.maxMs.toFixed(1), (e.totalMs / e.runs).toFixed(2)];
        });
        sections.push(`Instrumented effects total: ${total.toFixed(0)}ms`);
        sections.push(formatTable(["effect", "runs", "total(ms)", "max(ms)", "avg(ms)"], rows));
    }

    // Timing marks
    if (marks.size) {
        const entries = [...marks.entries()].sort((a, b) => a[1] - b[1]);
        const base = entries[0][1];
        let prev = base;
        const rows = [];
        for (const [name, t] of entries) {
            rows.push([name, (t - base).toFixed(1), (t - prev).toFixed(1)]);
            prev = t;
        }
        sections.push(formatTable(["mark", "t+(ms)", "Δprev(ms)"], rows));
    }

    return sections.join("\n\n");
}

export function dumpReportText() {
    const text = buildReportText();
    // eslint-disable-next-line no-console
    console.log(text);
    return text;
}

// Expose manual controls on window for ad-hoc use from the devtools console.
if (RENDER_PROFILING_ENABLED && typeof window !== "undefined") {
    window.__renderProfReport = dumpReport;
    window.__renderProfReset = profReset;
    window.__renderProfText = dumpReportText;
    // eslint-disable-next-line no-console
    console.info(
        "[renderProf] enabled — auto-dumps ~800ms after commits settle.\n" +
        "Console: __renderProfReport() (tables) • __renderProfReset() (clear) •\n" +
        "         copy(__renderProfText())  ← copies a plain-text report to your clipboard." +
        (VERBOSE ? " VERBOSE on." : ""),
    );
}
